import React from 'react';
import { CheckIcon, LockIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { ConfettiRain } from './ConfettiRain';
import { CTAButton } from './CTAButton';
import { DiscountBadge } from './PriceBlock';
import { MarketSelector } from './MarketSelector';
import { useMarket } from '../contexts/MarketContext';
import { images, PACK_VALUE_USD } from '../data/bonuses';
import { estimatedAmount, formatLocal } from '../utils/money';
export function Offer() {
  const {
    market
  } = useMarket();

  /* Estimated pack value and savings, always in the visitor's own currency. */
  const packValue = estimatedAmount(PACK_VALUE_USD, market);
  const packSavings = Math.max(packValue - market.priceAmount, 0);
  const stackTotal = formatLocal(packValue, market);
  const stackSavings = formatLocal(packSavings, market);
  return <section id="oferta" className="relative w-full overflow-hidden bg-cream py-11 sm:py-16" aria-labelledby="offer-title" data-id="element-161" data-mp="2fav4ark2i0sg">
      <ConfettiRain count={14} opacity={0.5} data-id="element-162" data-mp="pk7eriduof7v" />

      <div className="relative z-10 mx-auto max-w-content px-5 sm:px-8" data-id="element-163" data-mp="pjfkqbzwipjp">
        <Reveal data-id="element-164" data-mp="uisdtehd8suq">
          <h2 id="offer-title" className="text-center font-display text-2xl font-extrabold uppercase leading-tight text-navy sm:text-4xl" data-id="element-165" data-mp="ui8v3vjw60fn">
            Todo esto es <span className="text-brandgreen" data-id="element-166" data-mp="13k9vh1af92lj">tuyo hoy</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1} data-id="element-167" data-mp="6uio41h17kyb">
          <div className="mt-7 grid grid-cols-1 overflow-hidden rounded-[2rem] border border-navy/10 bg-white shadow-card lg:grid-cols-[0.95fr_1.05fr]" data-id="element-168" data-mp="6val9gqa1qqp">
            <div className="bg-cream2 p-6 sm:p-9" data-id="element-169" data-mp="funvibm86jch">
              <img src={images.bundle} alt="El Gran Reto Creativo con los 10 bonos gratis incluidos" className="mx-auto h-auto w-full max-w-[420px] rounded-3xl shadow-soft" width={1456} height={1092} loading="lazy" decoding="async" data-id="element-170" data-mp="fsukyl8hrez7" />
              <ul className="mx-auto mt-6 flex max-w-xs flex-col gap-2 sm:max-w-none" data-id="element-171" data-mp="vez0anxon90c">
                {['El Gran Reto Creativo · 365 actividades', '10 bonos GRATIS', 'Pasaporte Creativo'].map(i => <li key={i} className="flex items-center gap-2 font-sans text-sm text-ink" data-id="element-172" data-mp="106h55n2tttql">
                    <CheckIcon className="h-4 w-4 shrink-0 text-brandgreen" aria-hidden="true" data-id="element-173" data-mp="cgv4swapx8h0" />
                    {i}
                  </li>)}
              </ul>
            </div>

            <div className="p-6 text-center sm:p-9 lg:text-left" data-id="element-174" data-mp="lhkv5kh7m6nc">
              <div className="flex flex-wrap items-center justify-center gap-3 lg:justify-between" data-id="element-175" data-mp="liw03nme5kg1">
                <span className="font-display text-[11px] font-extrabold uppercase tracking-[0.18em] text-muted" data-id="element-176" data-mp="heo73e8whic0">
                  Precio en {market.country}
                </span>
                <MarketSelector tone="light" id="market-selector-offer" data-id="element-177" data-mp="1sucwec9mmtfz" />
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-3 lg:justify-start" data-id="element-178" data-mp="20duinxe99xg8">
                <span className="font-sans text-base text-muted line-through decoration-offer decoration-[2.5px]" data-id="element-179" data-mp="25fbhzoh3dbup">
                  {market.compareAt}
                </span>
                <span className="font-display text-5xl font-extrabold leading-none text-navy sm:text-6xl" data-id="element-180" data-mp="1vglzpula9pdx">
                  {market.price}
                </span>
                <DiscountBadge size="lg" data-id="element-181" data-mp="2o48bhq4o00bv" />
              </div>
              <p className="mt-3 font-sans text-sm font-semibold text-offer" data-id="element-182" data-mp="2o3or82rjkzuo">
                Ahorras {market.savings}
              </p>

              <div className="mt-7" data-id="element-183" data-mp="2eeezio0m1j73">
                <CTAButton fullWidth data-id="element-184" data-mp="2eddg9w9fxqea">Quiero todo por {market.price}</CTAButton>
              </div>

              <ul className="mx-auto mt-5 grid max-w-xs grid-cols-1 gap-2 sm:max-w-none sm:grid-cols-3" data-id="element-185" data-mp="322y8z6vec20r">
                {['Pago único', 'Acceso digital inmediato', 'Garantía 7 días'].map(i => <li key={i} className="flex items-center gap-1.5 font-sans text-xs text-muted" data-id="element-186" data-mp="1j7fvj835adnx">
                    <CheckIcon className="h-3.5 w-3.5 shrink-0 text-brandgreen" aria-hidden="true" data-id="element-187" data-mp="1988hsjsi0h0a" />
                    {i}
                  </li>)}
              </ul>

              {/* Estimated value vs. what you actually pay */}
              <div className="mt-6 rounded-2xl bg-cream p-5 text-left" data-id="element-188" data-mp="1wx9gvgffyfsw">
                <div className="flex items-baseline justify-between gap-4" data-id="element-189" data-mp="1x05graaunejz">
                  <span className="font-display text-xs font-extrabold uppercase text-muted" data-id="element-190" data-mp="1s59nk6tvml5a">
                    Valor estimado total
                  </span>
                  <span className="shrink-0 font-display text-sm font-extrabold text-muted line-through decoration-offer decoration-2" data-id="element-191" data-mp="2knnb9dli0qqn">
                    {stackTotal}
                  </span>
                </div>

                <div className="mt-2 flex items-baseline justify-between gap-4" data-id="element-192" data-mp="2o6mglzprw93h">
                  <span className="font-display text-xs font-extrabold uppercase text-navy" data-id="element-193" data-mp="2s64pty88evqh">
                    Hoy pagas
                  </span>
                  <span className="shrink-0 font-display text-base font-extrabold text-navy" data-id="element-194" data-mp="2x82ex93bckrc">
                    {market.price}
                  </span>
                </div>

                <div className="mt-3 flex items-baseline justify-between gap-4 rounded-xl bg-mint px-3 py-2" data-id="element-195" data-mp="373dsho2zjlxi">
                  <span className="font-display text-xs font-extrabold uppercase text-brandgreen" data-id="element-196" data-mp="3b3xsr0iuv5nv">
                    Te ahorras (valor estimado)
                  </span>
                  <span className="shrink-0 font-display text-base font-extrabold text-brandgreen" data-id="element-197" data-mp="3kuipeieyy045">
                    {stackSavings}
                  </span>
                </div>

                <p className="mt-3 font-sans text-[10px] leading-relaxed text-muted/70" data-id="element-198" data-mp="3ut882deovolg">
                  Valores estimados de referencia en {market.currency}.
                </p>
              </div>

              <p className="mt-5 flex items-center justify-center gap-1.5 font-sans text-[11px] text-muted lg:justify-start" data-id="element-199" data-mp="307174obnjwt1">
                <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-200" data-mp="3ew76ccahpn0x" />
                Pago seguro
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}